import React, { ReactNode } from 'react'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import Banner, { BANNER_COLOR_DANGER, BannerType } from '@divvy-web/skylab.banner'
import CollapsibleList from '@divvy-web/skylab.collapsiblelist'
import TextLink, { TextLinkSizes } from '@divvy-web/skylab.textlink'

import { useAuth } from '@sputnik/auth'
import Divider from '@sputnik/components/Divider'
import { useExperiment } from '@sputnik/experiments'

import styles from './styles.module.scss'

export interface SectionCollapsibleListProps {
  headerText: ReactNode
  onEditClick?: () => void
  hasMissingInfo: boolean
}

const SectionCollapsibleList = ({
  headerText,
  onEditClick,
  hasMissingInfo,
  children,
}: React.PropsWithChildren<SectionCollapsibleListProps>) => {
  const { email } = useAuth()
  const [getClassName, makeTestId] = useNameFormatter('SectionCollapsibleList')

  const { isInExperiment, behaviorName } = useExperiment({
    experiment: 'beep-credit-application-review-page-collapsed-experiment',
    inputId: email ?? '',
    bucketingKey: 'email',
  })

  const isMenuOpen =
    !isInExperiment || behaviorName === 'sectionsExpanded' || (behaviorName === 'sectionsCollapsed' && hasMissingInfo)

  return (
    <>
      <CollapsibleList
        isHeaderActive
        header={
          <div className={styles[getClassName('header')]}>
            <h3 className={styles[getClassName('header-title')]}>{headerText}</h3>
            {onEditClick && (
              <TextLink
                className={styles[getClassName('header-action')]}
                size={TextLinkSizes.REGULAR}
                onClick={onEditClick}
              >
                <FormattedMessage
                  defaultMessage='Edit'
                  id='sputnik.SectionCollapsibleList__wEQDC6'
                />
              </TextLink>
            )}
          </div>
        }
        menuIsOpen={isMenuOpen}
      >
        {hasMissingInfo && (
          <Banner
            backgroundColor={BANNER_COLOR_DANGER}
            bannerType={BannerType.Error}
            className={styles[getClassName('banner')]}
            dataTestId={makeTestId('missing-info')}
            messageContent={
              <FormattedMessage
                defaultMessage='Complete all fields marked with an asterisk to proceed.'
                id='sputnik.SectionCollapsibleList__61gZJm'
              />
            }
            titleText={
              <FormattedMessage
                defaultMessage='Required info missing'
                id='sputnik.SectionCollapsibleList__+UZ4VC'
              />
            }
          />
        )}
        <div className={styles[getClassName('fields')]}>{children}</div>
      </CollapsibleList>
      <Divider className={styles[getClassName('divider')]} />
    </>
  )
}

export default SectionCollapsibleList
